import type {
  ResponseResult,
  SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';

/**
 * Derive the bulk-answer cluster's "selected" state from the current
 * sub-question responses:
 *
 *   - Every sub-question has an entry AND every entry shares the
 *     same non-undefined result → return that result.
 *   - Anything else (missing entries, undefined result, or mixed
 *     results) → return undefined ("no bulk selection").
 *
 * Pure — extracted from `PerformAuditQuestionInteraction` to keep
 * the interaction under the file-size cap and to unit-test the
 * derived-uniform predicate on its own. This intentionally differs
 * from parent audit aggregation and write-side completion status:
 * bulk selection remains defined only for uniform child answers.
 */
export function deriveBulkSelectedResult(
  entries: readonly SubQuestionResponseEntry[] | undefined,
  totalSubQuestions: number,
): ResponseResult | undefined {
  if (!entries || entries.length < totalSubQuestions) return undefined;
  const first = entries.at(0)?.response?.responseResult;
  if (first === undefined) return undefined;
  for (const entry of entries) {
    if (entry.response?.responseResult !== first) return undefined;
  }
  return first;
}
